import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Calendar,
    MapPin,
    Plane,
    Users,
    FileText,
    XCircle,
    Download,
    ChevronLeft,
    CheckCircle,
    Clock,
    AlertCircle,
    CreditCard,
    Mail,
    Phone,
    Moon
} from 'lucide-react';
import {
    getReservationDetail,
    cancelReservation,
    getReservationDocuments,
    getReservationDocument
} from '../../services/tourvisioService';
import { useLocalization } from '../../hooks/useLocalization';
import type { ApiLogEntry } from '../../types';

interface BookingDetailProps {
    logApiCall?: (log: ApiLogEntry) => void;
}

const BookingDetail: React.FC<BookingDetailProps> = ({ logApiCall = () => { } }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useLocalization();

    const [booking, setBooking] = useState<any>(null);
    const [documents, setDocuments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);

    const fetchData = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const detail = await getReservationDetail(id, logApiCall);
            setBooking(detail);

            const docs = await getReservationDocuments(id, logApiCall);
            setDocuments(docs);
        } catch (err) {
            console.error('Failed to fetch booking detail:', err);
            setError('Failed to load booking details. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleCancel = async () => {
        if (!id || !cancelReason) return;
        setCancelling(true);
        try {
            const result = await cancelReservation(id, cancelReason, logApiCall);
            if (result.success) {
                setShowCancelModal(false);
                fetchData(); // Refresh data to show cancelled status
            } else {
                alert(result.error || 'Failed to cancel reservation');
            }
        } catch (err) {
            console.error('Cancellation error:', err);
            alert('An error occurred while cancelling the reservation.');
        } finally {
            setCancelling(false);
        }
    };

    const handleDownloadDoc = async (docType: string) => {
        if (!id) return;
        try {
            const doc = await getReservationDocument(id, docType, logApiCall);
            if (doc && (doc.url || doc.content)) {
                // If it's a URL, open it; if content, handle accordingly
                window.open(doc.url || doc.content, '_blank');
            } else {
                alert('Document not available yet.');
            }
        } catch (err) {
            console.error('Document download error:', err);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-gray-950 min-h-screen text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
                <p className="text-gray-400">Loading reservation details...</p>
            </div>
        );
    }

    if (error || !booking) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-gray-950 min-h-screen text-white px-4 text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                <h2 className="text-2xl font-bold mb-2">Oops!</h2>
                <p className="text-gray-400 mb-6">{error || 'Booking not found.'}</p>
                <button
                    onClick={() => navigate('/my-bookings')}
                    className="flex items-center gap-2 px-6 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 transition"
                >
                    <ChevronLeft className="w-4 h-4" /> Back to My Bookings
                </button>
            </div>
        );
    }

    const hotel = booking.services?.find((s: any) => s.type === 1) || {};
    const flights = booking.services?.filter((s: any) => s.type === 2) || [];
    const status = booking.status?.name || booking.status || 'Unknown';
    const statusStr = typeof status === 'string' ? status : String(status);
    const isCancelled = statusStr.toLowerCase().includes('cancel');
    const dbBooking = booking._dbBooking; // DB booking data if available

    // Safe date formatting
    const formatDate = (dateStr: any) => {
        if (!dateStr) return 'N/A';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return 'N/A';
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch { return 'N/A'; }
    };

    // Safe price formatting
    const formatPrice = (amount: any, currency: any) => {
        if (!amount && amount !== 0) return 'N/A';
        const num = Number(amount);
        if (isNaN(num)) return 'N/A';
        return `${num.toLocaleString()} ${currency || ''}`;
    };

    const hotelName = hotel.product?.name || hotel.name || dbBooking?.hotel?.name || 'Unknown Hotel';
    const roomType = hotel.description || hotel.roomName || dbBooking?.offer?.roomType || '';
    const boardName = hotel.board?.name || hotel.boardName || dbBooking?.offer?.board || '';
    const hotelCity = hotel.location?.city || hotel.city || dbBooking?.hotel?.location?.city || '';
    const hotelCountry = hotel.location?.country || hotel.country || dbBooking?.hotel?.location?.country || '';
    const nights = hotel.night || dbBooking?.offer?.nights || 0;
    const contactEmail = dbBooking?.contact?.email || booking.customerInfo?.email || '';
    const contactPhone = dbBooking?.contact?.phone || booking.customerInfo?.mobilePhone || '';

    return (
        <div className="bg-gray-950 min-h-screen pb-20 pt-10 px-4 md:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <button
                        onClick={() => navigate('/my-bookings')}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition"
                    >
                        <ChevronLeft className="w-5 h-5" /> Back to My Bookings
                    </button>
                    <div className="flex gap-3">
                        {!isCancelled && (
                            <button
                                onClick={() => setShowCancelModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-500 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition"
                            >
                                <XCircle className="w-4 h-4" /> Cancel Reservation
                            </button>
                        )}
                        <span className={`px-4 py-2 rounded-lg font-bold uppercase tracking-wider text-sm flex items-center gap-2 ${isCancelled ? 'bg-red-500/20 text-red-400' :
                                statusStr.toLowerCase() === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                                    'bg-yellow-500/20 text-yellow-400'
                            }`}>
                            {isCancelled ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            {statusStr}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Summary & Reservation Info */}
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl">
                            <h1 className="text-3xl font-black text-white mb-2">Reservation #{booking.reservationNumber}</h1>
                            <p className="text-gray-400 flex items-center gap-2 mb-6 text-sm">
                                <Clock className="w-4 h-4" /> Booked on {formatDate(booking.createDate || booking.createdAt)}
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-6 border-t border-gray-800">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-bold mb-1">Check-in</p>
                                    <p className="text-white font-bold">{formatDate(booking.beginDate)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-bold mb-1">Check-out</p>
                                    <p className="text-white font-bold">{formatDate(booking.endDate)}</p>
                                </div>
                                {nights > 0 && (
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase font-bold mb-1">Nights</p>
                                        <p className="text-white font-bold flex items-center gap-1"><Moon className="w-3 h-3" /> {nights}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-bold mb-1">Total Price</p>
                                    <p className="text-indigo-400 font-extrabold text-xl">
                                        {formatPrice(booking.totalPrice?.amount, booking.totalPrice?.currency)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Hotel Info */}
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden shadow-xl">
                            <div className="p-6">
                                <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
                                    <MapPin className="w-5 h-5 text-indigo-500" /> Hotel Details
                                </h2>
                                <h3 className="text-2xl font-black text-white mb-2">{hotelName}</h3>
                                {(hotelCity || hotelCountry) && (
                                    <p className="text-gray-400 text-sm mb-3 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" /> {[hotelCity, hotelCountry].filter(Boolean).join(', ')}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-4 text-gray-400 text-sm mb-4">
                                    {roomType && <span className="bg-gray-800 px-3 py-1 rounded-md">{roomType}</span>}
                                    {boardName && <span className="bg-gray-800 px-3 py-1 rounded-md">{boardName}</span>}
                                    {nights > 0 && <span className="bg-gray-800 px-3 py-1 rounded-md">{nights} Nights</span>}
                                </div>
                            </div>
                        </div>

                        {/* Flight Info */}
                        {flights.length > 0 && (
                            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl">
                                <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
                                    <Plane className="w-5 h-5 text-indigo-500" /> Flight Information
                                </h2>
                                <div className="space-y-6">
                                    {flights.map((flight: any, idx: number) => (
                                        <div key={idx} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                                            <div className="flex justify-between items-center mb-4">
                                                <span className="text-xs font-bold text-gray-500 uppercase">
                                                    {flight.arrival?.date > flight.departure?.date ? 'Return' : 'Outbound'}
                                                </span>
                                                <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                                                    {flight.airline?.name} • {flight.flightNo}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center px-4">
                                                <div className="text-center">
                                                    <p className="text-2xl font-black text-white">{new Date(flight.departure?.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                    <p className="text-xs text-gray-400">{flight.departure?.airport?.code}</p>
                                                </div>
                                                <div className="flex-1 px-8 relative">
                                                    <div className="h-px bg-gray-700 w-full relative">
                                                        <Plane className="w-3 h-3 text-indigo-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-90" />
                                                    </div>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-2xl font-black text-white">{new Date(flight.arrival?.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                                    <p className="text-xs text-gray-400">{flight.arrival?.airport?.code}</p>
                                                </div>
                                            </div>
                                            <p className="text-center text-xs text-gray-500 mt-4">
                                                {new Date(flight.departure?.date).toLocaleDateString()}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Passengers */}
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
                                <Users className="w-5 h-5 text-indigo-500" /> Travelers
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {booking.travellers?.map((pax: any, idx: number) => (
                                    <div key={idx} className="flex items-center gap-4 bg-gray-800/30 p-4 rounded-xl border border-gray-800">
                                        <div className="w-10 h-10 bg-indigo-500/20 text-indigo-400 rounded-full flex items-center justify-center font-bold">
                                            {pax.name?.[0]}{pax.surname?.[0]}
                                        </div>
                                        <div>
                                            <p className="text-white font-bold">{pax.name} {pax.surname}</p>
                                            <p className="text-xs text-gray-500">{pax.type === 1 ? 'Adult' : 'Child'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Documents Card */}
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl">
                            <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                                <FileText className="w-5 h-5 text-indigo-500" /> Documents
                            </h2>
                            <div className="space-y-3">
                                {documents.length > 0 ? documents.map((doc, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleDownloadDoc(doc.type)}
                                        className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-xl transition group"
                                    >
                                        <span className="flex items-center gap-3">
                                            <FileText className="w-4 h-4 text-gray-500 group-hover:text-indigo-400" />
                                            {doc.name || doc.type}
                                        </span>
                                        <Download className="w-4 h-4 text-gray-600 group-hover:text-white" />
                                    </button>
                                )) : (
                                    <div className="text-center py-6">
                                        <FileText className="w-10 h-10 text-gray-800 mx-auto mb-2" />
                                        <p className="text-xs text-gray-600">No documents generated yet.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Payment & Contact Info */}
                        {(contactEmail || contactPhone || booking.totalPrice?.amount) && (
                            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl">
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-indigo-500" /> Payment & Contact
                                </h2>
                                <div className="space-y-3">
                                    {booking.totalPrice?.amount > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs text-gray-500 uppercase font-bold">Total</span>
                                            <span className="text-indigo-400 font-bold">
                                                {formatPrice(booking.totalPrice?.amount, booking.totalPrice?.currency)}
                                            </span>
                                        </div>
                                    )}
                                    {dbBooking?.payment?.paymentStatus && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs text-gray-500 uppercase font-bold">Payment Status</span>
                                            <span className={`text-sm font-bold ${dbBooking.payment.paymentStatus === 'Completed' ? 'text-green-400' : 'text-yellow-400'}`}>
                                                {dbBooking.payment.paymentStatus}
                                            </span>
                                        </div>
                                    )}
                                    {contactEmail && (
                                        <div className="flex items-center gap-2 text-gray-300 text-sm pt-2 border-t border-gray-800">
                                            <Mail className="w-4 h-4 text-gray-500" />
                                            <span>{contactEmail}</span>
                                        </div>
                                    )}
                                    {contactPhone && (
                                        <div className="flex items-center gap-2 text-gray-300 text-sm">
                                            <Phone className="w-4 h-4 text-gray-500" />
                                            <span>{contactPhone}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Contact Card */}
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl">
                            <h3 className="text-lg font-bold text-white mb-4">Support</h3>
                            <p className="text-xs text-gray-400 leading-relaxed mb-6">
                                Need help with your reservation? Our customer support team is available 24/7 to assist with modifications or special requests.
                            </p>
                            <button className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition shadow-lg">
                                Contact Support
                            </button>
                        </div>
                    </div>
                </div>

                {/* Cancel Modal */}
                {showCancelModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                        <div className="bg-gray-900 border border-gray-800 rounded-3xl max-w-md w-full p-8 shadow-2xl">
                            <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                <XCircle className="w-8 h-8" />
                            </div>
                            <h2 className="text-2xl font-black text-white text-center mb-2">Cancel Reservation?</h2>
                            <p className="text-gray-400 text-center text-sm mb-6 leading-relaxed">
                                Are you sure you want to cancel this reservation? Cancellation policies and penalties may apply depending on the hotel policy.
                            </p>

                            <div className="mb-6">
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Reason for Cancellation</label>
                                <textarea
                                    className="w-full bg-gray-800 border border-gray-700 rounded-xl p-4 text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition"
                                    rows={3}
                                    placeholder="Please let us know why you're cancelling..."
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                />
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowCancelModal(false)}
                                    className="flex-1 py-3 bg-gray-800 text-gray-400 font-bold rounded-xl hover:bg-gray-700 transition"
                                >
                                    Go Back
                                </button>
                                <button
                                    onClick={handleCancel}
                                    disabled={cancelling || !cancelReason}
                                    className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                                >
                                    {cancelling ? <><div className="animate-spin h-4 w-4 border-2 border-white/20 border-t-white rounded-full"></div> Processing...</> : 'Confirm Cancel'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookingDetail;
